<script setup>
import FileTextIcon from '@iconify-vue/ph/file-text';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type } = defineProps({
    type: {
        type: String,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href:
                        route(
                            'front.projectManagement.customer.updateCustomer',
                            {
                                tenant: props.row.original.id,
                            },
                        ) + '?tab=budgets',
                    target: '_self',
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'seoPackageNetlinkingBudgetPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Netlinking',
                }),
            cell: (props) =>
                props.row.original.seoPackage
                    ? h(PriceCell, {
                          price: props.row.original.seoPackage
                              .netlinkingBudgetPerMonth,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'seaBudgetPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEA',
                }),
            cell: (props) =>
                props.row.original.seaBudgetPerMonth
                    ? h(PriceCell, {
                          price: props.row.original.seaBudgetPerMonth,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'smaBudgetPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMA',
                }),
            cell: (props) =>
                props.row.original.smaBudgetPerMonth
                    ? h(PriceCell, {
                          price: props.row.original.smaBudgetPerMonth,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'maxBudgetPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Budget<br/>Max/mois',
                }),
            cell: (props) =>
                props.row.original.maxBudgetPerMonth
                    ? h(PriceCell, {
                          price: props.row.original.maxBudgetPerMonth,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'roasTargetPercentage',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'PUB / CA %',
                }),
            cell: (props) =>
                props.row.original.roasTargetPercentage
                    ? h(NumberCell, {
                          number: props.row.original.roasTargetPercentage,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'grossMarginPercentage',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Marge<br/>Brute %',
                }),
            cell: (props) =>
                props.row.original.grossMarginPercentage
                    ? h(NumberCell, {
                          number: props.row.original.grossMarginPercentage,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                    width: 0,
                }),
            cell: (props) =>
                props.row.original.budgetNotes
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(
                                      DisplayContentAction,
                                      {
                                          title: 'Notes de budget',
                                          content:
                                              props.row.original.budgetNotes,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir les notes de budget',
                                                  icon: FileTextIcon,
                                                  type: 'primary',
                                              }),
                                      },
                                  ),
                          },
                      )
                    : null,
        }),
    ],
    url: route('front.projectManagement.customer.postBudgets'),
    data: {
        type,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    />
</template>
